import React, { useEffect, useState } from 'react';

/*
 * SafeImage
 * ---------
 * A hardened <img> wrapper used everywhere the app renders remote or
 * user-supplied imagery (blog covers, thumbnails, etc.).
 *
 * Guarantees:
 *   1. A missing / empty / malformed `src` never reaches the DOM — it is
 *      replaced by the MediaLabs brand placeholder before first paint.
 *   2. If a valid-looking URL fails to load at runtime, `onError` swaps in
 *      the same placeholder so a broken-image icon is NEVER shown.
 *   3. `src` changes are tracked (e.g. navigating between posts) so the
 *      fallback state resets correctly.
 */

export const BRAND_PLACEHOLDER = '/medialabs-preview-v3.png';

// Accept only absolute http(s) URLs or correct root-relative local paths.
const isSafeSrc = (value) =>
  typeof value === 'string' && /^(https?:\/\/|\/)[^\s]+$/.test(value.trim());

export const SafeImage = ({
  src,
  alt = '',
  className,
  fallback = BRAND_PLACEHOLDER,
  loading = 'lazy',
  ...rest
}) => {
  const resolveInitial = () => (isSafeSrc(src) ? src.trim() : fallback);
  const [imgSrc, setImgSrc] = useState(resolveInitial);

  // Reset when the incoming src (or fallback) changes.
  useEffect(() => {
    setImgSrc(isSafeSrc(src) ? src.trim() : fallback);
  }, [src, fallback]);

  const handleError = () => {
    if (imgSrc !== fallback) setImgSrc(fallback);
  };

  return (
    <img
      src={imgSrc}
      alt={alt}
      className={className}
      loading={loading}
      onError={handleError}
      {...rest}
    />
  );
};

export default SafeImage;
